import { sdk } from "@/lib/medusa/client";
import { ProductImage } from "@/types";
import { StoreProduct } from "@medusajs/types";

const MEDUSA_UNAVAILABLE_RETRY_DELAY = 30_000;

let medusaUnavailableUntil = 0;

const getErrorCode = (error: unknown): string | undefined => {
  if (!error || typeof error !== "object") {
    return undefined;
  }

  const code = "code" in error ? error.code : undefined;
  if (typeof code === "string") {
    return code;
  }

  const cause = "cause" in error ? error.cause : undefined;
  return getErrorCode(cause);
};

const getErrorStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== "object") {
    return undefined;
  }

  if ("status" in error && typeof error.status === "number") {
    return error.status;
  }

  if ("statusCode" in error && typeof error.statusCode === "number") {
    return error.statusCode;
  }

  if ("response" in error) {
    const responseStatus = getErrorStatus(error.response);
    if (responseStatus !== undefined) return responseStatus;
  }

  if ("cause" in error) {
    return getErrorStatus(error.cause);
  }

  return undefined;
};

const shouldSkipMedusaRequest = () => Date.now() < medusaUnavailableUntil;

const markMedusaUnavailable = (error: unknown) => {
  if (getErrorCode(error) !== "ECONNREFUSED") {
    return;
  }

  medusaUnavailableUntil = Date.now() + MEDUSA_UNAVAILABLE_RETRY_DELAY;
  console.info("Medusa backend is unavailable; returning empty product data briefly.");
};

/**
 * This function retrieves the featured images for a collection of Medusa products.
 * It returns an array of ProductImage objects, each containing the URL, alt text, source, and isFeatured status.
 */
export const getCollectionFeaturedImages = async (medusaProducts: StoreProduct[] | null) => {
  if (!medusaProducts || medusaProducts.length === 0) {
    return [];
  }

  const featuredImages: ProductImage[] = [];

  for (const medusaProduct of medusaProducts) {
    if (medusaProduct?.images && medusaProduct.images.length > 0) {
      featuredImages.push({
        url: medusaProduct.images[0].url,
        alt: medusaProduct.title || "Product image",
        source: "medusa",
        isFeatured: false,
      });
    }
  }

  return featuredImages;
};

/**
 * This function retrieves a medusa product by its ID and region.
 * @param id - The medusa ID of the product to retrieve.
 * @returns - The retrieved medusa product data.
 */
export const retrieveProductStrict = async (id: string): Promise<StoreProduct | null> => {
  try {
    const { product } = await sdk.store.product.retrieve(id, {
      fields: "*variants.calculated_price,+variants.inventory_quantity,+status,+metadata",
    });
    return product;
  } catch (storeError) {
    try {
      const result = await sdk.client.fetch<{
        data: StoreProduct;
      }>(`store/custom/products/all/${id}`);
      return result.data;
    } catch (fallbackError) {
      if (getErrorStatus(storeError) === 404 && getErrorStatus(fallbackError) === 404) {
        return null;
      }
      throw fallbackError;
    }
  }
};

export const retrieveProductByHandleStrict = async (handle: string): Promise<StoreProduct | null> => {
  const { products } = await sdk.store.product.list({
    handle,
    limit: 2,
    fields: "*variants.calculated_price,+variants.inventory_quantity,+status,+metadata",
  });

  return products.find((product) => product.handle === handle) || null;
};

export const retrieveProduct = async (id: string) => {
  if (shouldSkipMedusaRequest()) {
    return undefined;
  }

  try {
    return (await retrieveProductStrict(id)) ?? undefined;
  } catch (error) {
    markMedusaUnavailable(error);
    if (getErrorCode(error) !== "ECONNREFUSED") {
      console.warn(`Unable to retrieve Medusa product ${id}.`, error);
    }
    return undefined;
  }
};

export const getOrder = async (id: string) => {
  try {
    const { order } = await sdk.store.order.retrieve(id);
    return order;
  } catch (error) {
    console.error("Error retrieving order:", error);
  }
};
